import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { colors } from '../theme';

const About: React.FC = () => {
  const specialties = [
    'Repackaging VFDs',
    'Pump System Control Panels',
    'Small Programmable Logic Controller (PLC) & Human Machine Interface (HMI) Panels',
    'Engineered Chemical Storage Tanks & Secondary Containment Solutions',
    'Chemical Resistant Systems',
  ];

  const offerings = [
    'Onsite VFD Start-Up Services',
    'Panel Retrofit Services',
    'Harmonic Analysis',
    'Auto CAD Services',
    'Custom Fan Repair & Upgrades',
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Page Header */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            background: `linear-gradient(135deg, transparent 0%, rgba(245, 166, 35, 0.1) 100%)`,
          },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="overline"
              sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2, mb: 1, display: 'block' }}
            >
              Who We Are
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: '#FFFFFF',
                fontWeight: 700,
                mb: 2,
              }}
            >
              About Us
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '1.1rem',
                maxWidth: 500,
              }}
            >
              50+ years of expertise in electrical control solutions
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Accent Line */}
      <Box sx={{ height: 4, background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)` }} />

      {/* Main Content Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: '#FFFFFF',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{ color: colors.primary, fontWeight: 600, letterSpacing: 2 }}
              >
                Our Story
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  color: colors.secondary,
                  mb: 4,
                  mt: 1,
                }}
              >
                RPM & Process Control: Twice the Expertise
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  fontSize: '1.05rem',
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                Our core business focuses on <strong style={{ color: colors.secondary }}>connecting national and international electrical component suppliers with local customers</strong> at all opportunity levels, including industrial distributors, engineering firms, and OEM's. We measure success in the quality and quantity of local relationships created and maintained over the last 50 years.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  fontSize: '1.05rem',
                  lineHeight: 1.9,
                  mb: 3,
                }}
              >
                Combining our <strong style={{ color: colors.secondary }}>sales expertise with our onsite product application</strong> support has dramatically increased results for our customers, and they have responded with loyalty, commitment, and appreciation.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: colors.textSecondary,
                  fontSize: '1.05rem',
                  lineHeight: 1.9,
                  mb: 4,
                }}
              >
                <strong style={{ color: colors.secondary }}>RPM Control and Process Control are ready to help.</strong> We provide a broad range of services while offering assistance in choosing the right electrical control product or control panel features. We also provide onsite troubleshooting on existing variable frequency drive (VFDs) or control panels.
              </Typography>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
              >
                Get in Touch
              </Button>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 24,
                    left: 24,
                    right: -24,
                    bottom: -24,
                    border: `3px solid ${colors.primary}`,
                    borderRadius: 4,
                    zIndex: -1,
                  },
                }}
              >
                <Box
                  component="img"
                  src="/vfd-panel-1.jpg"
                  alt="About Who We Are"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Specialties and Offerings Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: colors.background,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Our Specialties */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  border: 'none',
                  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08)',
                  overflow: 'visible',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                    borderRadius: '16px 16px 0 0',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: '1.5rem',
                      color: colors.secondary,
                      mb: 3,
                      fontWeight: 700,
                    }}
                  >
                    Our Specialties
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {specialties.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                        }}
                      >
                        <CheckCircleOutlineIcon sx={{ color: colors.primary, fontSize: 22, mt: 0.25 }} />
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.textSecondary,
                            lineHeight: 1.6,
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* We Offer */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  border: 'none',
                  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.08)',
                  overflow: 'visible',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)`,
                    borderRadius: '16px 16px 0 0',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: '1.5rem',
                      color: colors.secondary,
                      mb: 3,
                      fontWeight: 700,
                    }}
                  >
                    We Offer
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {offerings.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 2,
                        }}
                      >
                        <CheckCircleOutlineIcon sx={{ color: colors.primary, fontSize: 22, mt: 0.25 }} />
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.textSecondary,
                            lineHeight: 1.6,
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Core Business CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 12 },
          background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondaryDark} 100%)`,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'rgba(245, 166, 35, 0.15)',
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Typography sx={{ fontSize: '3rem' }}>🌍</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Card
                sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 4,
                }}
              >
                <CardContent sx={{ p: { xs: 4, md: 5 } }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { xs: '1.5rem', md: '1.75rem' },
                      color: '#FFFFFF',
                      mb: 3,
                      fontWeight: 700,
                      lineHeight: 1.4,
                    }}
                  >
                    Our core business is representing quality manufacturers from around the world that can help various companies in Upstate NY.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.85)',
                      fontSize: '1.05rem',
                      lineHeight: 1.9,
                      mb: 4,
                    }}
                  >
                    We can help you with VFDs, along with various control-panel components such as wire way, power supplies, FRP enclosures, disconnects, motor starters, pilot devices, small PLC systems, and HMIs of all sizes with remote access options. We also offer field service assistance.
                  </Typography>
                  <Button
                    component={Link}
                    to="/suppliers"
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                  >
                    View Our Suppliers
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
